import moment from 'moment';

export function getPricesCalendar(rentalRuleset, { standard_capacity, maximum_capacity, from, to }) {
  if (!standard_capacity || !maximum_capacity) return {};
  const result = {};
  const seasonalPrices = (rentalRuleset.seasonal_prices || []).filter(price => {
    return price.date_start <= to && price.date_end >= from;
  });
  const fullSeasonalPrices = fillGapsSeasonsWithBasePrice(seasonalPrices, { rentalRuleset, from, to });
  let lastType = 'base';
  fullSeasonalPrices.forEach(seasonalPrice => {
    // For displaying colors in calendar
    seasonalPrice.display_type = 'base';
    if (seasonalPrice.isSeason) {
      if (lastType === 'base' || lastType === 'season_alter') {
        seasonalPrice.display_type = 'season';
      } else {
        seasonalPrice.display_type = 'season_alter';
      }
    }
    lastType = seasonalPrice.display_type;
    // get prices
    const pricesByMinNightsAndNbGuests = formatSeasonalPriceForCalendar(seasonalPrice, { standard_capacity, maximum_capacity });
    Object.keys(pricesByMinNightsAndNbGuests).forEach(date => {
      // 'key' is `${minNights}_${nbGuests}
      Object.keys(pricesByMinNightsAndNbGuests[date]).forEach(key => {
        // set prices by date
        const prices = pricesByMinNightsAndNbGuests[date][key];
        result[key] = result[key] || {};
        result[key][date] = prices;
      });
    });
  });
  return result;
}

export function getConditionsCalendar(rentalRuleset, { from, to }) {
  const defaultMinStay = (rentalRuleset.default_conditions || {}).min_stay;
  if (!defaultMinStay) return [];
  const result = [];
  const seasonalConditions = (rentalRuleset.seasonal_conditions || []).filter(cond => {
    return cond.date_start <= to && cond.date_end >= from;
  });
  const fullSeasonalConditions = fillGapsSeasonsWithMinStay(seasonalConditions, { defaultMinStay, from, to });
  let lastType = 'base';
  fullSeasonalConditions.forEach(seasonalCondition => {
    // For displaying colors in calendar
    seasonalCondition.display_type = 'base';
    if (seasonalCondition.isSeason) {
      if (lastType === 'base' || lastType === 'season_alter') {
        seasonalCondition.display_type = 'season';
      } else {
        seasonalCondition.display_type = 'season_alter';
      }
    }
    lastType = seasonalCondition.display_type;
    // Put condition for day
    let start = seasonalCondition.date_start;
    let end = seasonalCondition.date_end;
    if (start && end) {
      if (start < from) start = from;
      if (end > to) end = to;
      for (let currentDay = start; currentDay <= end; currentDay = moment(currentDay).add(1, 'days').format('YYYY-MM-DD')) {
        result.push({ ...seasonalCondition, date: currentDay, display_type: seasonalCondition.display_type, seasonStart: seasonalCondition.date_start, seasonEnd: seasonalCondition.date_end });
      }
    }
  });
  return result;
}

export function getAvailabilitiesCalendar(property, unitsAvailabilityDays, { from, to }) {
  const result = [];
  for (let currentDay = from; currentDay <= to; currentDay = moment(currentDay).add(1, 'days').format('YYYY-MM-DD')) {
    const available = unitsAvailabilityDays.some(unitAvailability => {
      const day = unitAvailability.days.find(d => d.date === currentDay);
      return day.availability > 0;
    });
    result.push(available);
  }
  return result;
}

function fillGapsSeasonsWithBasePrice(seasonalPrices, { rentalRuleset, from, to }, takeAll = 1) {
  const prices = seasonalPrices.filter(p => !!p && !p.deleted).map(p => ({ ...p, isSeason: true }));
  let lastDateStart = from;
  const { base_price, base_extra, price_variability, day_of_week_prices } = (rentalRuleset.default_price || {});
  prices.forEach(price => {
    if (price.date_start > lastDateStart) { // Is gap
      const dateEnd = moment(price.date_start).subtract(1, 'days').format('YYYY-MM-DD');
      prices.push({ isSeason: false, date_start: lastDateStart, date_end: dateEnd, base_price, base_extra, price_variability, day_of_week_prices });
    }
    lastDateStart = moment(price.date_end).add(1, 'days').format('YYYY-MM-DD');
  });
  if (lastDateStart <= to) { // Fill gap at the end
    // do not create seaons priced at 0
    if (base_price || base_extra || takeAll) {
      prices.push({ isSeason: false, date_start: lastDateStart, date_end: to, base_price, base_extra, price_variability, day_of_week_prices });
    }
  }
  return prices.sort((a, b) => a.date_start.localeCompare(b.date_start));
}

function fillGapsSeasonsWithMinStay(seasonalConditions, { defaultMinStay, from, to }) {
  const conditions = seasonalConditions.filter(p => !!p && !p.deleted).map(cond => ({ ...cond, isSeason: true }));
  let lastDateStart = from;
  conditions.forEach(condition => {
    if (condition.date_start > lastDateStart) { // Is gap
      const dateEnd = moment(condition.date_start).subtract(1, 'days').format('YYYY-MM-DD');
      conditions.push({ date_start: lastDateStart, date_end: dateEnd, min_stay: defaultMinStay, isSeason: false });
    }
    lastDateStart = moment(condition.date_end).add(1, 'days').format('YYYY-MM-DD');
  });
  if (lastDateStart <= to) { // Fill gap at the end
    conditions.push({ date_start: lastDateStart, date_end: to, min_stay: defaultMinStay, isSeason: false });
  }
  return conditions.sort((a, b) => a.date_start.localeCompare(b.date_start));
}

function formatSeasonalPriceForCalendar(seasonalPrice, { standard_capacity, maximum_capacity }) {
  const result = {};
  const start = seasonalPrice.date_start;
  const end = seasonalPrice.date_end;
  if (!start || !end) return {};
  // For each date
  for (let currentDay = start; currentDay <= end; currentDay = moment(currentDay).add(1, 'days').format('YYYY-MM-DD')) {
    const pricesByMinNightsAndNbGuests = getPricesByMinNightsAndNbGuests(seasonalPrice, { currentDay, standard_capacity, maximum_capacity });
    // 'key' is `${minNights}_${nbGuests}`
    Object.keys(pricesByMinNightsAndNbGuests).forEach(key => {
      result[currentDay] = result[currentDay] || {};
      const params = pricesByMinNightsAndNbGuests[key];
      result[currentDay][key] = { price: params.price, min_nights: params.min_nights, nb_guests: params.nb_guests, date: currentDay, display_type: seasonalPrice.display_type, seasonStart: start, seasonEnd: end };
    });
  }
  return result;
}
function getPricesByMinNightsAndNbGuests(seasonalPrice, { currentDay, standard_capacity, maximum_capacity }) {
  const result = {};
  // Base price for 1 night
  let variationsMinNights = [{ min_nights: 1, value: 100 }];
  // Variations for nb nights
  const variations = (seasonalPrice.variations_for_min_nights || []).slice();
  variations.sort((a, b) => a.min_nights - b.min_nights);
  variationsMinNights = variationsMinNights.concat(variations);
  // Get extras
  const { base_price, base_extra } = getPricesOfDate(seasonalPrice, currentDay);
  const extraForExtraGuests = seasonalPrice.extra_for_extra_guests;
  const extraByNgGuest = getExtrasByNbGuests({ maximum_capacity, standard_capacity, baseExtra: base_extra, extraForExtraGuests });
  // Get variations
  variationsMinNights.forEach(params => {
    const minNights = parseInt(params.min_nights);
    // Get prices by nb guests
    for (let nbGuests = 1; nbGuests <= maximum_capacity; nbGuests++) {
      const key = `${minNights}_${nbGuests}`;
      const extra = parseFloat(extraByNgGuest[nbGuests] || 0);
      const price = applyMinNightsVariation(base_price + extra, params);
      result[key] = { price, min_nights: minNights, nb_guests: nbGuests };
    }
  });
  return result;
}

function applyMinNightsVariation(amount, paramsMinNights = null) {
  const minNightsVariation = paramsMinNights ? parseFloat(paramsMinNights.value) : 100;
  return amount * minNightsVariation / 100;
}

function getExtrasByNbGuests({ maximum_capacity, standard_capacity, baseExtra = 0, extraForExtraGuests = null }) {
  const result = {};
  let lastExtra = 0;
  for (let nbGuests = 1; nbGuests <= maximum_capacity; nbGuests++) {
    const extraGuests = nbGuests - standard_capacity;
    if (extraGuests <= 0) { // no extra
      result[nbGuests] = 0;
    } else { // get extra
      const extraCharge = extraForExtraGuests ? extraForExtraGuests.find(params => parseInt(params.nb_extra_guests) === extraGuests) : null;
      const extra = extraCharge ? parseFloat(extraCharge.charge || 0) : lastExtra + baseExtra;
      lastExtra = extra;
      result[nbGuests] = extra;
    }
  }
  return result;
}
function getPricesOfDate(seasonalPrice, date) {
  const { base_price, base_extra, day_of_week_prices } = seasonalPrice;
  if (seasonalPrice.price_variability && seasonalPrice.day_of_week_prices) {
    const numDay = moment(date).day() || 7;
    return getPricesFromDayOfWeek(numDay, { base_price, base_extra, day_of_week_prices });
  }
  // Default price
  const result = { base_price, base_extra };
  result.base_price = parseFloat(result.base_price || 0);
  result.base_extra = parseFloat(result.base_extra || 0);
  return result;
}

function getPricesFromDayOfWeek(numDay, { base_price, base_extra, day_of_week_prices }) {
  const result = { base_price, base_extra };
  const dayOfWeekPrice = day_of_week_prices.find(p => parseInt(p.day_of_week) === numDay);
  if (dayOfWeekPrice) {
    if (!isNaN(parseInt(dayOfWeekPrice.price))) result.base_price = dayOfWeekPrice.price;
    if (!isNaN(parseInt(dayOfWeekPrice.extra))) result.base_extra = dayOfWeekPrice.extra;
  }
  result.base_price = parseFloat(result.base_price || 0);
  result.base_extra = parseFloat(result.base_extra || 0);
  return result;
}



// WEBPACK FOOTER //
// ./src/frontend/modules/daily-rental/rental-ruleset/components/RentalRulesetEditor/helpers.js